import type { CampaignItem } from '../types/campaigns.types';

const SENDER_META_PREFIX = 'mc:sender:';

export type CampaignSenderMeta = {
  fromName: string;
  replyTo: string;
  preheader: string;
};

export const DEFAULT_SENDER_META: CampaignSenderMeta = {
  fromName: '',
  replyTo: '',
  preheader: '',
};

export const parseCampaignSenderMeta = (
  tags: string[] | null | undefined
): CampaignSenderMeta => {
  const raw = (tags ?? []).find((tag) => tag.startsWith(SENDER_META_PREFIX));
  if (!raw) return { ...DEFAULT_SENDER_META };

  try {
    const parsed = JSON.parse(raw.slice(SENDER_META_PREFIX.length)) as Partial<CampaignSenderMeta>;
    return {
      fromName: typeof parsed.fromName === 'string' ? parsed.fromName : '',
      replyTo: typeof parsed.replyTo === 'string' ? parsed.replyTo : '',
      preheader: typeof parsed.preheader === 'string' ? parsed.preheader : '',
    };
  } catch {
    return { ...DEFAULT_SENDER_META };
  }
};

export const upsertCampaignSenderMeta = (
  tags: string[] | null | undefined,
  meta: CampaignSenderMeta
): string[] => {
  const without = (tags ?? []).filter((tag) => !tag.startsWith(SENDER_META_PREFIX));
  const payload: CampaignSenderMeta = {
    fromName: meta.fromName.trim(),
    replyTo: meta.replyTo.trim(),
    preheader: meta.preheader.trim(),
  };
  if (!payload.fromName && !payload.replyTo && !payload.preheader) {
    return without;
  }
  return [...without, `${SENDER_META_PREFIX}${JSON.stringify(payload)}`];
};

export const blankCampaignHtml = (campaignName: string): string => {
  const title = campaignName.trim() || 'Campaign';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title></head><body style="margin:0;padding:24px;font-family:Inter,Arial,sans-serif;color:#27272C;background:#ffffff"><p style="margin:0;font-size:16px;line-height:1.5">Start writing your email…</p><p style="margin:24px 0 0;font-size:12px;line-height:1.5;color:#707070"><a href="{{unsubscribeUrl}}">Unsubscribe</a></p></body></html>`;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const getCampaignHtml = (campaign: CampaignItem | null | undefined): string => {
  const html = campaign?.CampaignMediaDetails?.find((media) => media.Templet?.trim())?.Templet?.trim();
  return html ?? '';
};

/** Default merge tags for MailCraft Variables panel (HTML-in/HTML-out; tokens pass through). */
export const CAMPAIGN_MERGE_TAG_VARIABLES =
  'first_name,last_name,email,unsubscribeUrl,preferencesUrl';
